import { API_BASE, get, post } from '../api';

interface GuestSession {
  guest_slug: string;
  guest_label: string;
  server_id: string;
  text_channel_id: string;
  voice_channel_id: string;
  text_channel_name: string;
  voice_channel_name: string;
}

interface GuestMessage {
  id: string;
  server_id: string;
  channel_id: string;
  channel: string;
  author_id: string;
  author_name: string;
  content: string;
  created_at: string;
  read_by_guest: boolean;
}

interface VoiceToken {
  url: string;
  token: string;
  room: string;
}

interface LinkStatus {
  guest_slug: string;
  guest_label: string;
  status: string;
}

type GuestMode = 'setup' | 'verify' | 'disabled' | 'home' | 'text' | 'call';
type CallStatus = 'idle' | 'calling' | 'connected' | 'reconnecting' | 'ended' | 'error';

let session: GuestSession | null = null;
let mode: GuestMode = 'setup';
let pinOne = '';
let pinTwo = '';
let errorText = '';
let requestMessage = '';
let requestSent = false;
let callStatus: CallStatus = 'idle';
let callMuted = false;
let speakerOn = true;
let roomHandle: { disconnect: () => Promise<void>; setMuted: (muted: boolean) => Promise<void> } | null = null;

export function isNamedGuestPath(): boolean {
  const path = strippedPath();
  return path === '/ma' || path === '/james';
}

export async function renderNamedGuestPage(): Promise<void> {
  mountStyles();
  document.body.classList.add('named-guest-body');
  try {
    session = await get<GuestSession>('/api/named-guests/session');
    mode = 'home';
  } catch {
    session = null;
    const token = tokenFromUrl();
    if (token) {
      const link = await get<LinkStatus>(`/api/named-guests/link-status?token=${encodeURIComponent(token)}`).catch(() => null);
      mode = link && link.status !== 'active' && link.status !== 'pending_pin' ? 'disabled' : 'setup';
    } else {
      mode = 'setup';
    }
  }
  paint();
}

function strippedPath(): string {
  const path = window.location.pathname;
  return API_BASE && path.startsWith(API_BASE) ? path.slice(API_BASE.length) || '/' : path;
}

function routeLabel(): string {
  return strippedPath().replace('/', '') === 'james' ? 'James' : 'Ma';
}

function tokenFromUrl(): string {
  return new URLSearchParams(window.location.search).get('token')?.trim() || '';
}

function paint(): void {
  const app = document.getElementById('app');
  if (!app) return;
  app.innerHTML = `<main class="ng-shell">${renderCurrent()}</main>`;
  wire();
}

function renderCurrent(): string {
  if (mode === 'home' && session) return renderHome(session);
  if (mode === 'text' && session) return renderText(session);
  if (mode === 'call' && session) return renderCall(session);
  if (mode === 'disabled') return renderDisabled();
  return renderPin();
}

function renderDisabled(): string {
  const label = routeLabel();
  return `
    <section class="ng-auth">
      <div class="ng-auth-head">
        <h1>Access is currently disabled</h1>
        <p>${escapeHtml(label)}</p>
      </div>
      <div class="ng-notice">Access is controlled by Mitchell</div>
      <label class="ng-label" for="ng-request-message">Message to Mitchell</label>
      <textarea id="ng-request-message" class="ng-message-box" maxlength="1000" rows="5">${escapeHtml(requestMessage)}</textarea>
      ${requestSent ? '<div class="ng-success">Request sent.</div>' : ''}
      ${errorText ? `<div class="ng-error">${escapeHtml(errorText)}</div>` : ''}
      <button class="ng-primary" id="ng-request-access" type="button">Request access</button>
    </section>
  `;
}

function renderPin(): string {
  const label = routeLabel();
  const isSetup = mode === 'setup';
  return `
    <section class="ng-auth">
      <div class="ng-auth-head">
        <h1>${isSetup ? 'Set up your access' : 'Enter your PIN'}</h1>
        <p>${escapeHtml(label)}</p>
      </div>
      <div class="ng-notice">Screenshots are not permitted in this guest channel.</div>
      <label class="ng-label" for="ng-pin-one">${isSetup ? 'New 6-digit PIN' : '6-digit PIN'}</label>
      <input id="ng-pin-one" class="ng-pin-input" type="password" inputmode="numeric" autocomplete="one-time-code" maxlength="6" value="${escapeAttr(pinOne)}" />
      ${isSetup ? `
        <label class="ng-label" for="ng-pin-two">Confirm PIN</label>
        <input id="ng-pin-two" class="ng-pin-input" type="password" inputmode="numeric" autocomplete="one-time-code" maxlength="6" value="${escapeAttr(pinTwo)}" />
      ` : ''}
      ${errorText ? `<div class="ng-error">${escapeHtml(errorText)}</div>` : ''}
      <button class="ng-primary" id="ng-pin-submit" type="button">${isSetup ? 'Set PIN' : 'Continue'}</button>
      <button class="ng-link-btn" id="ng-mode-toggle" type="button">${isSetup ? 'I already have a PIN' : 'Set up PIN instead'}</button>
      ${isSetup ? '' : '<div class="ng-help">Forgot PIN? Contact Mitchell</div>'}
    </section>
  `;
}

function renderHome(s: GuestSession): string {
  return `
    <section class="ng-home">
      <header class="ng-top">
        <h1>${escapeHtml(s.guest_label)}</h1>
      </header>
      <div class="ng-card-grid">
        <button class="ng-action-card" id="ng-open-text" type="button">
          ${iconMessage()}
          <span>${escapeHtml(s.text_channel_name)}</span>
        </button>
        <button class="ng-action-card" id="ng-open-call" type="button">
          ${iconPhone()}
          <span>${escapeHtml(s.voice_channel_name)}</span>
          <small>WebRTC encrypted media</small>
        </button>
      </div>
    </section>
  `;
}

function renderText(s: GuestSession): string {
  return `
    <section class="ng-chat">
      <header class="ng-view-head">
        <button class="ng-icon-btn" id="ng-back-home" type="button" aria-label="Back">${iconArrowLeft()}</button>
        <h1>${escapeHtml(s.text_channel_name)}</h1>
      </header>
      <div class="ng-notice">Read receipts enabled</div>
      <div class="ng-notice">Screenshots are not permitted in this guest channel.</div>
      <div class="ng-messages" id="ng-messages">Loading...</div>
      <form class="ng-compose" id="ng-compose">
        <input id="ng-message-input" type="text" autocomplete="off" maxlength="4000" placeholder="Message" />
        <button type="submit" aria-label="Send">${iconSend()}</button>
      </form>
    </section>
  `;
}

function renderCall(s: GuestSession): string {
  return `
    <section class="ng-call">
      <header class="ng-view-head">
        <button class="ng-icon-btn" id="ng-back-home" type="button" aria-label="Back">${iconArrowLeft()}</button>
        <h1>${escapeHtml(s.voice_channel_name)}</h1>
      </header>
      <div class="ng-call-stage">
        <div class="ng-call-status">${escapeHtml(callStatusText())}</div>
        <div class="ng-call-security">Encrypted in transit</div>
      </div>
      <div class="ng-call-controls">
        ${callStatus === 'connected'
          ? `<button class="ng-round ${callMuted ? 'ng-round-active' : ''}" id="ng-mute" type="button" aria-label="Mute">${iconMic()}</button>
             <button class="ng-round ${speakerOn ? 'ng-round-active' : ''}" id="ng-speaker" type="button" aria-label="Speaker">${iconVolume()}</button>
             <button class="ng-round ng-end" id="ng-end-call" type="button" aria-label="End call">${iconPhoneOff()}</button>`
          : `<button class="ng-primary ng-join" id="ng-join-call" type="button">Join call</button>`}
      </div>
    </section>
  `;
}

function callStatusText(): string {
  if (callStatus === 'calling') return 'Calling';
  if (callStatus === 'connected') return 'Connected';
  if (callStatus === 'reconnecting') return 'Reconnecting';
  if (callStatus === 'ended') return 'Ended';
  if (callStatus === 'error') return 'Unable to connect';
  return 'Ready';
}

function wire(): void {
  document.getElementById('ng-mode-toggle')?.addEventListener('click', () => {
    mode = mode === 'setup' ? 'verify' : 'setup';
    pinOne = '';
    pinTwo = '';
    errorText = '';
    paint();
  });
  document.getElementById('ng-pin-one')?.addEventListener('input', (e) => {
    pinOne = digitsOnly((e.target as HTMLInputElement).value);
    (e.target as HTMLInputElement).value = pinOne;
  });
  document.getElementById('ng-pin-two')?.addEventListener('input', (e) => {
    pinTwo = digitsOnly((e.target as HTMLInputElement).value);
    (e.target as HTMLInputElement).value = pinTwo;
  });
  document.getElementById('ng-pin-submit')?.addEventListener('click', () => {
    void submitPin();
  });
  document.getElementById('ng-request-message')?.addEventListener('input', (e) => {
    requestMessage = (e.target as HTMLTextAreaElement).value;
  });
  document.getElementById('ng-request-access')?.addEventListener('click', () => {
    void submitAccessRequest();
  });
  document.getElementById('ng-open-text')?.addEventListener('click', () => {
    mode = 'text';
    paint();
    void loadMessages();
  });
  document.getElementById('ng-open-call')?.addEventListener('click', () => {
    mode = 'call';
    paint();
  });
  document.getElementById('ng-back-home')?.addEventListener('click', () => {
    mode = 'home';
    paint();
  });
  document.getElementById('ng-compose')?.addEventListener('submit', (e) => {
    e.preventDefault();
    void sendMessage();
  });
  document.getElementById('ng-join-call')?.addEventListener('click', () => {
    void joinCall();
  });
  document.getElementById('ng-end-call')?.addEventListener('click', () => {
    void endCall();
  });
  document.getElementById('ng-mute')?.addEventListener('click', () => {
    void toggleMute();
  });
  document.getElementById('ng-speaker')?.addEventListener('click', () => {
    speakerOn = !speakerOn;
    paint();
  });
  if (mode === 'text') {
    document.addEventListener('visibilitychange', onVisibilityChange, { once: true });
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden' && mode === 'text') {
    void post('/api/named-guests/messages/read', {
      channel_id: session?.text_channel_id || '',
      message_ids: [],
    }).catch(() => undefined);
  }
}

async function submitPin(): Promise<void> {
  const token = tokenFromUrl();
  if (!token) {
    errorText = 'Link token missing.';
    paint();
    return;
  }
  if (!/^\d{6}$/.test(pinOne)) {
    errorText = 'Enter a 6-digit PIN.';
    paint();
    return;
  }
  if (mode === 'setup' && pinOne !== pinTwo) {
    errorText = 'PIN entries do not match.';
    paint();
    return;
  }
  try {
    session = await post<GuestSession>(mode === 'setup' ? '/api/named-guests/setup-pin' : '/api/named-guests/verify-pin', {
      token,
      pin: pinOne,
    });
    mode = 'home';
    pinOne = '';
    pinTwo = '';
    errorText = '';
  } catch (err) {
    errorText = err instanceof Error ? err.message : 'PIN check failed.';
    if (mode === 'setup' && errorText.toLowerCase().includes('unavailable')) mode = 'verify';
  }
  paint();
}

async function submitAccessRequest(): Promise<void> {
  const message = requestMessage.trim();
  if (!message) {
    errorText = 'Enter a message for Mitchell.';
    paint();
    return;
  }
  try {
    await post('/api/named-guests/request-access', {
      guest_slug: routeLabel().toLowerCase(),
      message,
    });
    requestSent = true;
    errorText = '';
  } catch (err) {
    errorText = err instanceof Error ? err.message : 'Request failed.';
  }
  paint();
}

async function loadMessages(): Promise<void> {
  if (!session) return;
  const box = document.getElementById('ng-messages');
  if (!box) return;
  try {
    const rows = await get<GuestMessage[]>(`/api/named-guests/messages?channel_id=${encodeURIComponent(session.text_channel_id)}&limit=50`);
    box.innerHTML = rows.slice().reverse().map(renderMessage).join('');
    const ids = rows.map(r => r.id);
    if (ids.length > 0) {
      await post('/api/named-guests/messages/read', {
        channel_id: session.text_channel_id,
        message_ids: ids,
      });
    }
  } catch (err) {
    box.innerHTML = `<div class="ng-error">${escapeHtml(err instanceof Error ? err.message : 'Messages failed to load')}</div>`;
  }
}

function renderMessage(m: GuestMessage): string {
  return `
    <article class="ng-message">
      <div class="ng-message-meta">
        <strong>${escapeHtml(m.author_name)}</strong>
        <time>${escapeHtml(formatTime(m.created_at))}</time>
        <span class="ng-read" title="${m.read_by_guest ? 'Read' : 'Sent'}">${m.read_by_guest ? iconDoubleCheck() : iconCheck()}</span>
      </div>
      <div class="ng-message-content">${escapeHtml(m.content)}</div>
    </article>
  `;
}

async function sendMessage(): Promise<void> {
  if (!session) return;
  const input = document.getElementById('ng-message-input') as HTMLInputElement | null;
  const content = input?.value.trim() || '';
  if (!content) return;
  await post<GuestMessage>('/api/named-guests/messages/send', {
    channel_id: session.text_channel_id,
    content,
  });
  if (input) input.value = '';
  await loadMessages();
}

async function joinCall(): Promise<void> {
  if (!session) return;
  callStatus = 'calling';
  paint();
  try {
    const resp = await get<VoiceToken>('/api/named-guests/voice-token');
    const mod = await import('livekit-client');
    const room = new mod.Room();
    room.on(mod.RoomEvent.Reconnecting, () => {
      callStatus = 'reconnecting';
      paint();
    });
    room.on(mod.RoomEvent.Reconnected, () => {
      callStatus = 'connected';
      paint();
    });
    room.on(mod.RoomEvent.Disconnected, () => {
      callStatus = 'ended';
      paint();
    });
    await room.connect(resp.url, resp.token);
    const audioTrack = await mod.createLocalAudioTrack();
    await room.localParticipant.publishTrack(audioTrack);
    roomHandle = {
      disconnect: async () => {
        audioTrack.stop();
        room.disconnect();
      },
      setMuted: async (muted: boolean) => {
        if (muted) await audioTrack.mute();
        else await audioTrack.unmute();
      },
    };
    callMuted = false;
    callStatus = 'connected';
  } catch {
    callStatus = 'error';
  }
  paint();
}

async function endCall(): Promise<void> {
  if (roomHandle) await roomHandle.disconnect();
  roomHandle = null;
  callStatus = 'ended';
  paint();
}

async function toggleMute(): Promise<void> {
  callMuted = !callMuted;
  await roomHandle?.setMuted(callMuted);
  paint();
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6);
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function escapeHtml(value: string): string {
  const el = document.createElement('div');
  el.textContent = value;
  return el.innerHTML;
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function svg(body: string): string {
  return `<svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
}

function iconMessage(): string { return svg('<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>'); }
function iconPhone(): string { return svg('<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.11 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.91.33 1.8.62 2.64a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.44-1.19a2 2 0 0 1 2.11-.45c.84.29 1.73.5 2.64.62A2 2 0 0 1 22 16.92z"/>'); }
function iconPhoneOff(): string { return svg('<path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.44-1.19a2 2 0 0 1 2.11-.45c.84.29 1.73.5 2.64.62A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-3.33-2.67M2 2l20 20M4.11 2h3a2 2 0 0 1 2 1.72c.12.91.33 1.8.62 2.64a2 2 0 0 1-.45 2.11L8.09 9.91"/>'); }
function iconArrowLeft(): string { return svg('<line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>'); }
function iconSend(): string { return svg('<line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>'); }
function iconMic(): string { return svg('<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/>'); }
function iconVolume(): string { return svg('<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>'); }
function iconCheck(): string { return svg('<polyline points="20 6 9 17 4 12"/>'); }
function iconDoubleCheck(): string { return svg('<polyline points="20 6 9 17 4 12"/><polyline points="16 6 8 14 4 10"/>'); }

function mountStyles(): void {
  if (document.getElementById('named-guest-styles')) return;
  const style = document.createElement('style');
  style.id = 'named-guest-styles';
  style.textContent = `
.named-guest-body #app { min-height: 100dvh; background: var(--bg); color: var(--fg); user-select: none; }
.ng-shell { min-height: 100dvh; display: flex; justify-content: center; padding: 20px; }
.ng-auth, .ng-home, .ng-chat, .ng-call { width: min(100%, 520px); min-height: calc(100dvh - 40px); display: flex; flex-direction: column; }
.ng-auth { justify-content: center; gap: 12px; }
.ng-auth-head h1, .ng-top h1, .ng-view-head h1 { margin: 0; color: var(--fg); letter-spacing: 0; }
.ng-auth-head p { margin: 6px 0 0; color: var(--fg-muted); }
.ng-notice { padding: 10px 12px; border: 1px solid var(--border); background: var(--surface-1); border-radius: 8px; color: var(--fg-muted); font-size: 13px; line-height: 1.45; }
.ng-label { font-size: 13px; color: var(--fg-muted); }
.ng-pin-input, .ng-compose input, .ng-message-box { width: 100%; box-sizing: border-box; border: 1px solid var(--border); background: var(--surface-1); color: var(--fg); border-radius: 8px; padding: 14px 16px; font-size: 20px; letter-spacing: 0; }
.ng-message-box { min-height: 140px; resize: vertical; font-size: 15px; line-height: 1.45; user-select: text; }
.ng-primary, .ng-link-btn, .ng-action-card, .ng-compose button, .ng-icon-btn, .ng-round { border: 1px solid var(--border); border-radius: 8px; background: var(--surface-1); color: var(--fg); cursor: pointer; }
.ng-primary { background: var(--accent); color: var(--fg-on-accent); padding: 14px 16px; font-weight: 700; }
.ng-link-btn { padding: 10px 12px; }
.ng-help, .ng-error, .ng-success { color: var(--fg-muted); font-size: 13px; }
.ng-error { color: var(--danger); }
.ng-success { color: var(--success); }
.ng-top, .ng-view-head { display: flex; align-items: center; gap: 10px; padding: 8px 0 16px; }
.ng-card-grid { display: grid; gap: 12px; margin-top: 12px; }
.ng-action-card { min-height: 132px; display: grid; justify-items: start; align-content: center; gap: 10px; padding: 18px; text-align: left; }
.ng-action-card span { font-size: 20px; font-weight: 700; }
.ng-action-card small { color: var(--fg-muted); }
.ng-icon-btn { width: 44px; height: 44px; display: grid; place-items: center; flex: 0 0 auto; }
.ng-messages { flex: 1; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; padding: 14px 0 88px; }
.ng-message { padding: 12px; border: 1px solid var(--border); background: var(--surface-1); border-radius: 8px; }
.ng-message-meta { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--fg-muted); }
.ng-message-meta strong { color: var(--fg); }
.ng-read { margin-left: auto; display: inline-flex; color: var(--accent); }
.ng-read svg { width: 16px; height: 16px; }
.ng-message-content { margin-top: 6px; line-height: 1.45; user-select: none; overflow-wrap: anywhere; }
.ng-compose { position: fixed; left: 20px; right: 20px; bottom: calc(20px + var(--keyboard-offset, 0px)); display: flex; gap: 8px; max-width: 520px; margin: 0 auto; }
.ng-compose button { width: 54px; display: grid; place-items: center; }
.ng-call { width: min(100%, 680px); }
.ng-call-stage { flex: 1; display: grid; place-items: center; align-content: center; gap: 12px; }
.ng-call-status { font-size: 30px; font-weight: 800; }
.ng-call-security { color: var(--fg-muted); }
.ng-call-controls { display: flex; justify-content: center; gap: 18px; padding: 24px 0; }
.ng-round { width: 70px; height: 70px; display: grid; place-items: center; border-radius: 50%; }
.ng-round-active { border-color: var(--accent); color: var(--accent); }
.ng-end { background: var(--danger); color: var(--fg-on-accent); }
.ng-join { width: min(100%, 260px); }
@media (min-width: 720px) {
  .ng-card-grid { grid-template-columns: 1fr 1fr; }
  .ng-compose { left: 50%; right: auto; transform: translateX(-50%); width: min(100% - 40px, 520px); }
}
@supports (bottom: env(keyboard-inset-height)) {
  @media (max-width: 768px) {
    .ng-compose { bottom: calc(20px + env(keyboard-inset-height, 0px)); }
  }
}
`;
  document.head.appendChild(style);
}
